import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaintBrushIcon,
  PauseCircleIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import { platformAPI } from '../../services/api';

type CompanyStatus = 'active' | 'suspended' | 'marked_for_delete';
type StripeState = 'ready' | 'incomplete' | 'none';
type AccessState = 'approved' | 'pending' | 'rejected' | 'none';
type PlatformFeeMode = 'inherit' | 'custom';

type PlatformFeePolicySummary = {
  policy_id: string | null;
  configured_mode: PlatformFeeMode;
  source: 'tenant_custom' | 'platform_default' | 'environment_fallback';
  rate_basis_points: number;
  rate_percent: number;
  effective_from: string | null;
  agreement_reference?: string | null;
  reason?: string | null;
};

type CompanyInfo = {
  address?: string;
  support_email?: string;
  tax_id?: string;
  invoice_prefix?: string;
  timezone?: string;
};

type Company = {
  id: string;
  name: string;
  slug: string;
  status: CompanyStatus;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  app_name?: string | null;
  favicon_url?: string | null;
  email_sending_enabled?: boolean;
  farm_out_enabled?: boolean;
  stripe_account_id?: string | null;
  stripe_charges_enabled?: boolean;
  platform_fee_policy?: PlatformFeePolicySummary;
  company_info?: CompanyInfo | null;
  created_at: string;
  updated_at: string;
};

type AccessRequest = {
  id: string;
  company_id: string;
  status: string;
};

type PlatformUser = {
  id: string;
  email: string;
  role: string;
  company_id?: string | null;
  company_name?: string | null;
};

const INITIAL_CREATE_FORM = {
  name: '',
  slug: '',
  operatorAdminEmail: '',
  primary_color: '',
  secondary_color: '',
  app_name: '',
  favicon_url: '',
};

const INITIAL_EDIT_FORM = {
  name: '',
  logo_url: '',
  primary_color: '',
  secondary_color: '',
  app_name: '',
  favicon_url: '',
  address: '',
  support_email: '',
  tax_id: '',
  invoice_prefix: '',
  timezone: '',
  status: 'active' as CompanyStatus,
  email_sending_enabled: true,
  farm_out_enabled: false,
};

const INITIAL_FEE_FORM = {
  mode: 'inherit' as PlatformFeeMode,
  ratePercent: '5',
  effectiveFrom: '',
  agreementReference: '',
  reason: '',
};

function getBaseHost(): string {
  if (typeof window === 'undefined') return 'modalos.com';

  const segments = window.location.hostname.split('.');
  return segments.length > 1 ? segments.slice(1).join('.') : window.location.hostname;
}

function getTenantHost(slug: string): string {
  return `${slug}.${getBaseHost()}`;
}

function getCompanyUrl(slug: string): string {
  if (typeof window === 'undefined') return `https://${slug}.modalos.com`;

  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${getTenantHost(slug)}${port}`;
}

function getCompanyInfo(company: Company): CompanyInfo {
  return company.company_info || {};
}

function hasBranding(company: Company): boolean {
  return Boolean(
    company.logo_url ||
      company.primary_color ||
      company.secondary_color ||
      company.app_name ||
      company.favicon_url,
  );
}

function getStripeState(company: Company): StripeState {
  if (company.stripe_charges_enabled) return 'ready';
  if (company.stripe_account_id) return 'incomplete';
  return 'none';
}

function normalizeAccessState(status?: string | null): AccessState {
  if (status === 'approved') return 'approved';
  if (status === 'pending') return 'pending';
  if (status === 'rejected') return 'rejected';
  return 'none';
}

function getInitials(name: string): string {
  const parts = name
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) return 'CO';
  return parts.map((part) => part[0]?.toUpperCase() || '').join('');
}

function formatDate(value?: string): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
}

function getStatusBadgeClass(status: CompanyStatus): string {
  if (status === 'active') {
    return 'border border-outline/70 bg-success-container text-on-success-container';
  }
  if (status === 'marked_for_delete') {
    return 'border border-outline/70 bg-error-container text-on-error-container';
  }
  return 'border border-outline/70 bg-warning-container text-on-warning-container';
}

function getStripeBadgeClass(state: StripeState): string {
  if (state === 'ready') return 'border border-outline/70 bg-success-container text-on-success-container';
  if (state === 'incomplete') return 'border border-outline/70 bg-warning-container text-on-warning-container';
  return 'border border-outline/70 bg-surface-container-low text-on-surface';
}

function getAccessBadgeClass(state: AccessState): string {
  if (state === 'approved') return 'border border-outline/70 bg-success-container text-on-success-container';
  if (state === 'pending') return 'border border-outline/70 bg-warning-container text-on-warning-container';
  if (state === 'rejected') return 'border border-outline/70 bg-error-container text-on-error-container';
  return 'border border-outline/70 bg-surface-container-low text-on-surface';
}

function getStripeLabel(state: StripeState): string {
  if (state === 'ready') return 'Billing ready';
  if (state === 'incomplete') return 'Stripe started';
  return 'No Stripe account';
}

function getAccessLabel(state: AccessState): string {
  if (state === 'approved') return 'Access approved';
  if (state === 'pending') return 'Access pending';
  if (state === 'rejected') return 'Access rejected';
  return 'No access request';
}

function AdminCompanies() {
  const queryClient = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | CompanyStatus>('all');
  const [stripeFilter, setStripeFilter] = useState<'all' | StripeState>('all');
  const [accessFilter, setAccessFilter] = useState<'all' | AccessState>('all');
  const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingSlug, setEditingSlug] = useState('');
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [feeForm, setFeeForm] = useState(INITIAL_FEE_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  const { data: companies = [], isLoading: companiesLoading } = useQuery(
    'platform-companies',
    () => platformAPI.getCompanies(),
    { select: (response) => response.data },
  );

  const { data: myAccessRequests = [] } = useQuery(
    'platform-access-requests',
    () => platformAPI.getAccessRequests(),
    { select: (response) => response.data },
  );

  const { data: platformUsers = [], isLoading: usersLoading } = useQuery(
    'platform-users-rollup',
    () => platformAPI.getUsers(),
    { select: (response) => response.data },
  );

  const { data: feePolicyDetails, isLoading: feePolicyLoading } = useQuery(
    ['platform-company-fee-policy', editingId],
    () => platformAPI.getCompanyPlatformFeePolicy(editingId as string),
    {
      enabled: Boolean(editingId),
      select: (response) => response.data,
    },
  );

  const createMutation = useMutation((data: typeof INITIAL_CREATE_FORM) => platformAPI.createCompany(data), {
    onSuccess: (response) => {
      const invite = response.data?.onboardingInvite;
      toast.success('Company created');
      if (invite?.emailDelivery?.success) {
        toast.success('Tenant onboarding email sent');
      } else {
        toast.error(invite?.emailDelivery?.error || 'Tenant onboarding email was not delivered');
      }
      setShowCreate(false);
      setCreateForm(INITIAL_CREATE_FORM);
      queryClient.invalidateQueries('platform-companies');
      queryClient.invalidateQueries('platform-users-rollup');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create company');
    },
  });

  const updateMutation = useMutation(
    (data: { id: string; updates: typeof INITIAL_EDIT_FORM }) =>
      platformAPI.updateCompany(data.id, data.updates),
    {
      onSuccess: () => {
        toast.success('Company updated');
        setEditingId(null);
        setEditingName('');
        setEditingSlug('');
        setEditForm(INITIAL_EDIT_FORM);
        queryClient.invalidateQueries('platform-companies');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update company');
      },
    },
  );

  const feePolicyMutation = useMutation(
    (data: { id: string; policy: Parameters<typeof platformAPI.updateCompanyPlatformFeePolicy>[1] }) =>
      platformAPI.updateCompanyPlatformFeePolicy(data.id, data.policy),
    {
      onSuccess: () => {
        toast.success('Platform fee policy saved');
        queryClient.invalidateQueries('platform-companies');
        queryClient.invalidateQueries(['platform-company-fee-policy', editingId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to save platform fee policy');
      },
    },
  );

  const suspendMutation = useMutation((id: string) => platformAPI.suspendCompany(id), {
    onSuccess: () => {
      toast.success('Company suspended');
      queryClient.invalidateQueries('platform-companies');
    },
    onError: () => {
      toast.error('Failed to suspend company');
    },
  });

  const activateMutation = useMutation((id: string) => platformAPI.activateCompany(id), {
    onSuccess: () => {
      toast.success('Company activated');
      queryClient.invalidateQueries('platform-companies');
    },
    onError: () => {
      toast.error('Failed to activate company');
    },
  });

  const softDeleteMutation = useMutation((id: string) => platformAPI.softDeleteCompany(id), {
    onSuccess: () => {
      toast.success('Company marked for deletion. Reconciliation is required before permanent delete.');
      setDeleteTarget(null);
      setDeletePermanent(false);
      setDeleteConfirmName('');
      queryClient.invalidateQueries('platform-companies');
      queryClient.invalidateQueries('platform-users-rollup');
    },
    onError: () => {
      toast.error('Failed to delete company');
    },
  });

  const permanentDeleteMutation = useMutation((id: string) => platformAPI.permanentDeleteCompany(id), {
    onSuccess: () => {
      toast.success('Company permanently deleted');
      setDeleteTarget(null);
      setDeletePermanent(false);
      setDeleteConfirmName('');
      queryClient.invalidateQueries('platform-companies');
      queryClient.invalidateQueries('platform-users-rollup');
    },
    onError: () => {
      toast.error('Failed to permanently delete company');
    },
  });

  const requestAccessMutation = useMutation((companyId: string) => platformAPI.requestCompanyAccess(companyId), {
    onSuccess: () => {
      toast.success('Access requested');
      queryClient.invalidateQueries('platform-access-requests');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to request access');
    },
  });

  const uploadLogoMutation = useMutation(
    (data: { id: string; file: File }) => platformAPI.uploadCompanyLogo(data.id, data.file),
    {
      onSuccess: (response) => {
        const logoUrl = response.data?.logo_url || response.data?.url || '';
        setEditForm((current) => ({ ...current, logo_url: logoUrl }));
        toast.success('Logo uploaded');
        queryClient.invalidateQueries('platform-companies');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to upload logo');
      },
    },
  );

  const companyIds = new Set(companies.map((company: Company) => company.id));
  const companyScopedUsers = platformUsers.filter(
    (user: PlatformUser) => user.company_id && companyIds.has(user.company_id),
  );

  const userCountByCompany: Record<string, number> = {};
  const adminCountByCompany: Record<string, number> = {};
  companyScopedUsers.forEach((user: PlatformUser) => {
    if (!user.company_id) return;
    userCountByCompany[user.company_id] = (userCountByCompany[user.company_id] || 0) + 1;
    if (user.role === 'admin') {
      adminCountByCompany[user.company_id] = (adminCountByCompany[user.company_id] || 0) + 1;
    }
  });

  const accessStatusByCompany: Record<string, AccessState> = {};
  myAccessRequests.forEach((request: AccessRequest) => {
    if (!accessStatusByCompany[request.company_id]) {
      accessStatusByCompany[request.company_id] = normalizeAccessState(request.status);
    }
  });

  const totalCompanies = companies.length;
  const activeCompanies = companies.filter((company: Company) => company.status === 'active').length;
  const billingReadyCompanies = companies.filter(
    (company: Company) => getStripeState(company) === 'ready',
  ).length;
  const brandedCompanies = companies.filter((company: Company) => hasBranding(company)).length;
  const pendingAccessRequests = myAccessRequests.filter(
    (request: AccessRequest) => normalizeAccessState(request.status) === 'pending',
  ).length;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredCompanies = companies.filter((company: Company) => {
    const companyInfo = getCompanyInfo(company);
    const stripeState = getStripeState(company);
    const accessState = accessStatusByCompany[company.id] || 'none';

    const matchesSearch =
      !normalizedSearchTerm ||
      [
        company.name,
        company.slug,
        company.app_name || '',
        companyInfo.support_email || '',
        companyInfo.invoice_prefix || '',
        companyInfo.timezone || '',
      ].some((value) => value.toLowerCase().includes(normalizedSearchTerm));

    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesStripe = stripeFilter === 'all' || stripeState === stripeFilter;
    const matchesAccess = accessFilter === 'all' || accessState === accessFilter;

    return matchesSearch && matchesStatus && matchesStripe && matchesAccess;
  });

  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();
    createMutation.mutate({
      name: createForm.name,
      slug: createForm.slug,
      operatorAdminEmail: createForm.operatorAdminEmail,
      primary_color: createForm.primary_color,
      secondary_color: createForm.secondary_color,
      app_name: createForm.app_name,
      favicon_url: createForm.favicon_url,
    });
  };

  const startEdit = (company: Company) => {
    const companyInfo = getCompanyInfo(company);
    setEditingId(company.id);
    setEditingName(company.name);
    setEditingSlug(company.slug);
    setEditForm({
      name: company.name,
      logo_url: company.logo_url || '',
      primary_color: company.primary_color || '',
      secondary_color: company.secondary_color || '',
      app_name: company.app_name || '',
      favicon_url: company.favicon_url || '',
      address: companyInfo.address || '',
      support_email: companyInfo.support_email || '',
      tax_id: companyInfo.tax_id || '',
      invoice_prefix: companyInfo.invoice_prefix || '',
      timezone: companyInfo.timezone || '',
      status: company.status,
      email_sending_enabled: company.email_sending_enabled !== false,
      farm_out_enabled: company.farm_out_enabled === true,
    });
    setFeeForm({
      ...INITIAL_FEE_FORM,
      mode: company.platform_fee_policy?.configured_mode || 'inherit',
      ratePercent: company.platform_fee_policy
        ? String(company.platform_fee_policy.rate_percent)
        : '',
    });
  };

  const saveEdit = () => {
    if (!editingId) return;

    updateMutation.mutate({
      id: editingId,
      updates: {
        name: editForm.name,
        logo_url: editForm.logo_url,
        primary_color: editForm.primary_color,
        secondary_color: editForm.secondary_color,
        app_name: editForm.app_name,
        favicon_url: editForm.favicon_url,
        address: editForm.address,
        support_email: editForm.support_email,
        tax_id: editForm.tax_id,
        invoice_prefix: editForm.invoice_prefix,
        timezone: editForm.timezone,
        status: editForm.status,
        email_sending_enabled: editForm.email_sending_enabled,
        farm_out_enabled: editForm.farm_out_enabled,
      },
    });
  };

  const saveFeePolicy = () => {
    if (!editingId) return;
    feePolicyMutation.mutate({
      id: editingId,
      policy: {
        mode: feeForm.mode,
        ratePercent: feeForm.mode === 'custom' ? feeForm.ratePercent : null,
        effectiveFrom: feeForm.effectiveFrom
          ? new Date(feeForm.effectiveFrom).toISOString()
          : null,
        agreementReference: feeForm.agreementReference || null,
        reason: feeForm.reason || null,
      },
    });
  };

  const uploadCompanyLogo = (file?: File) => {
    if (!editingId || !file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB');
      return;
    }
    uploadLogoMutation.mutate({ id: editingId, file });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;

    if (deletePermanent) {
      if (deleteConfirmName !== deleteTarget.name) {
        toast.error('Company name does not match');
        return;
      }
      permanentDeleteMutation.mutate(deleteTarget.id);
      return;
    }

    softDeleteMutation.mutate(deleteTarget.id);
  };

  const tenantPreviewHost = createForm.slug
    ? getTenantHost(createForm.slug)
    : `your-company.${getBaseHost()}`;
  const previewFeeRate = feeForm.mode === 'custom'
    ? Number(feeForm.ratePercent || 0)
    : feePolicyDetails?.effective?.rate_percent;
  const previewFeeAmount = typeof previewFeeRate === 'number' && Number.isFinite(previewFeeRate)
    ? previewFeeRate * 10
    : null;

  return (
    <PageLayout className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-on-surface">Platform Companies</h1>
          <p className="mt-2 max-w-3xl text-on-surface-variant">
            Operate tenant lifecycle, monitor billing readiness, and tighten access from one surface instead of bouncing between sparse records.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((current) => !current)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>{showCreate ? 'Close Create Form' : 'Create Company'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Companies</p>
            <p className="text-xl font-bold text-on-surface">{totalCompanies}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Active Tenants</p>
            <p className="text-xl font-bold text-on-surface">{activeCompanies}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <CreditCardIcon className="h-8 w-8 text-amber-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Billing Ready</p>
            <p className="text-xl font-bold text-on-surface">{billingReadyCompanies}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <UsersIcon className="h-8 w-8 text-violet-600" />
          <div>
            <p className="text-sm text-on-surface-variant">Managed Users</p>
            <p className="text-xl font-bold text-on-surface">
              {usersLoading ? '...' : companyScopedUsers.length}
            </p>
          </div>
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-2.5 text-on-surface-variant" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="input-field pl-10"
              placeholder="Search by tenant, slug, support email..."
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | CompanyStatus)}
            className="input-field"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="marked_for_delete">Marked for delete</option>
          </select>
          <select
            value={stripeFilter}
            onChange={(event) => setStripeFilter(event.target.value as 'all' | StripeState)}
            className="input-field"
          >
            <option value="all">All billing states</option>
            <option value="ready">Billing ready</option>
            <option value="incomplete">Stripe started</option>
            <option value="none">No Stripe account</option>
          </select>
          <select
            value={accessFilter}
            onChange={(event) => setAccessFilter(event.target.value as 'all' | AccessState)}
            className="input-field"
          >
            <option value="all">All access states</option>
            <option value="approved">Access approved</option>
            <option value="pending">Access pending</option>
            <option value="rejected">Access rejected</option>
            <option value="none">No access request</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-info-container text-on-info-container font-medium">
            {filteredCompanies.length} visible
          </span>
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-success-container text-on-success-container font-medium">
            {brandedCompanies} branded tenants
          </span>
          <span className="inline-flex px-2.5 py-1 rounded-full border border-outline/70 bg-warning-container text-on-warning-container font-medium">
            {pendingAccessRequests} pending access requests
          </span>
        </div>
      </div>

      {showCreate && (
        <div className="card p-6 space-y-5 border border-outline/70">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-on-surface">Create Company</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Provision a tenant plus its initial operator admin in one step.
              </p>
            </div>
            <div className="rounded-lg border border-outline/70 bg-info-container px-3 py-2 text-xs text-on-info-container font-medium">
              Tenant host preview: {tenantPreviewHost}
            </div>
          </div>

          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Name</label>
              <input
                value={createForm.name}
                onChange={(event) => setCreateForm({ ...createForm, name: event.target.value })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Slug</label>
              <input
                value={createForm.slug}
                onChange={(event) => setCreateForm({ ...createForm, slug: event.target.value })}
                className="input-field"
                placeholder="acme"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Operator Admin Email</label>
              <input
                type="email"
                value={createForm.operatorAdminEmail}
                onChange={(event) =>
                  setCreateForm({ ...createForm, operatorAdminEmail: event.target.value })
                }
                className="input-field"
                required
              />
            </div>

            <div className="md:col-span-2 rounded-lg border border-outline/70 bg-info-container px-4 py-3 text-sm text-on-info-container">
              The tenant admin will receive a one-time password setup email and will be guided into onboarding after they sign in.
            </div>

            <div className="md:col-span-2 pt-2 border-t border-outline/70">
              <div className="flex items-center gap-2 mb-3">
                <PaintBrushIcon className="h-5 w-5 text-violet-600" />
                <h3 className="text-sm font-semibold text-on-surface">Branding</h3>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">App Name</label>
              <input
                value={createForm.app_name}
                onChange={(event) => setCreateForm({ ...createForm, app_name: event.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-10 w-14 rounded cursor-pointer"
                  value={createForm.primary_color || '#2563eb'}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, primary_color: event.target.value })
                  }
                />
                <input
                  value={createForm.primary_color}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, primary_color: event.target.value })
                  }
                  className="input-field flex-1"
                  placeholder="#2563eb"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-10 w-14 rounded cursor-pointer"
                  value={createForm.secondary_color || '#6b7280'}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, secondary_color: event.target.value })
                  }
                />
                <input
                  value={createForm.secondary_color}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, secondary_color: event.target.value })
                  }
                  className="input-field flex-1"
                  placeholder="#6b7280"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Favicon URL</label>
              <input
                value={createForm.favicon_url}
                onChange={(event) => setCreateForm({ ...createForm, favicon_url: event.target.value })}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false);
                  setCreateForm(INITIAL_CREATE_FORM);
                }}
                className="px-4 py-2 text-sm font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-highest hover:text-on-surface rounded-md"
              >
                Cancel
              </button>
              <button type="submit" disabled={createMutation.isLoading} className="btn-primary">
                {createMutation.isLoading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </div>
      )}

      {editingId && (
        <div className="card p-6 space-y-5 border border-outline/70">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-on-surface">Edit {editingName}</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Update lifecycle status, company operations metadata, and branding for {editingSlug}.
              </p>
            </div>
            <button
              onClick={() => {
                setEditingId(null);
                setEditingName('');
                setEditingSlug('');
                setEditForm(INITIAL_EDIT_FORM);
                setFeeForm(INITIAL_FEE_FORM);
              }}
              className="text-on-surface-variant hover:text-on-surface"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Name</label>
              <input
                value={editForm.name}
                onChange={(event) => setEditForm({ ...editForm, name: event.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Status</label>
              <select
                value={editForm.status}
                onChange={(event) =>
                  setEditForm({ ...editForm, status: event.target.value as CompanyStatus })
                }
                className="input-field"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="marked_for_delete">Marked for delete</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Support Email</label>
              <input
                type="email"
                value={editForm.support_email}
                onChange={(event) =>
                  setEditForm({ ...editForm, support_email: event.target.value })
                }
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Timezone</label>
              <select
                value={editForm.timezone}
                onChange={(event) => setEditForm({ ...editForm, timezone: event.target.value })}
                className="input-field"
              >
                <option value="">Default</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
            <label className="md:col-span-2 flex items-start gap-3 rounded-lg border border-outline p-4">
              <input type="checkbox" checked={editForm.farm_out_enabled}
                onChange={event => setEditForm({ ...editForm, farm_out_enabled: event.target.checked })} />
              <span><span className="block font-medium">Approved for farm-outs</span>
                <span className="block text-sm">Allow this active operator to offer reservations and respond to other operators. Existing handoff history remains available when disabled.</span></span>
            </label>
            <label className="md:col-span-2 flex items-start gap-3 rounded-lg border border-outline/70 bg-surface-container-low px-4 py-3">
              <input
                type="checkbox"
                checked={editForm.email_sending_enabled}
                onChange={(event) =>
                  setEditForm({ ...editForm, email_sending_enabled: event.target.checked })
                }
                className="mt-1 rounded border-outline text-primary focus:ring-primary"
              />
              <span>
                <span className="block text-sm font-medium text-on-surface">Email sending enabled</span>
                <span className="block text-xs text-on-surface-variant">
                  Turn this off to stop platform-triggered tenant emails for this company.
                </span>
              </span>
            </label>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Tax ID</label>
              <input
                value={editForm.tax_id}
                onChange={(event) => setEditForm({ ...editForm, tax_id: event.target.value })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Invoice Prefix</label>
              <input
                value={editForm.invoice_prefix}
                onChange={(event) =>
                  setEditForm({ ...editForm, invoice_prefix: event.target.value })
                }
                className="input-field"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Address</label>
              <textarea
                value={editForm.address}
                onChange={(event) => setEditForm({ ...editForm, address: event.target.value })}
                className="input-field"
                rows={2}
              />
            </div>

            <div className="md:col-span-2 pt-2 border-t border-outline/70">
              <div className="flex items-center gap-2 mb-3">
                <CreditCardIcon className="h-5 w-5 text-primary" />
                <h3 className="text-sm font-semibold text-on-surface">Platform fee policy</h3>
              </div>
              <p className="mb-4 text-sm text-on-surface-variant">
                Applies to future fee commitments. Existing committed invoices and payment requests retain their original rate.
              </p>
              <div className="inline-flex rounded-md border border-outline/70 p-1" role="group" aria-label="Platform fee mode">
                {(['inherit', 'custom'] as PlatformFeeMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setFeeForm({ ...feeForm, mode })}
                    className={`px-3 py-1.5 text-sm font-medium rounded ${
                      feeForm.mode === mode
                        ? 'bg-primary text-on-primary'
                        : 'text-on-surface-variant hover:bg-surface-container-low'
                    }`}
                  >
                    {mode === 'inherit' ? 'Use platform default' : 'Custom rate'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={feeForm.ratePercent}
                disabled={feeForm.mode === 'inherit'}
                onChange={(event) => setFeeForm({ ...feeForm, ratePercent: event.target.value })}
                className="input-field disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Effective date</label>
              <input
                type="datetime-local"
                value={feeForm.effectiveFrom}
                onChange={(event) => setFeeForm({ ...feeForm, effectiveFrom: event.target.value })}
                className="input-field"
              />
              <p className="mt-1 text-xs text-on-surface-variant">Leave blank to apply immediately.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Agreement reference</label>
              <input
                value={feeForm.agreementReference}
                onChange={(event) => setFeeForm({ ...feeForm, agreementReference: event.target.value })}
                className="input-field"
                placeholder="Contract or approval reference"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Reason</label>
              <input
                value={feeForm.reason}
                onChange={(event) => setFeeForm({ ...feeForm, reason: event.target.value })}
                className="input-field"
                placeholder="Why this rate applies"
              />
            </div>
            <div className="md:col-span-2 flex flex-col gap-3 border-b border-outline/70 pb-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-on-surface-variant">
                <span className="font-medium text-on-surface">Sample:</span>{' '}
                {previewFeeAmount === null
                  ? 'Effective policy unavailable.'
                  : `$${previewFeeAmount.toFixed(2)} platform fee on $1,000.00 pretax.`}
                {feePolicyDetails?.effective && (
                  <span className="ml-2">
                    Current: {feePolicyDetails.effective.rate_percent}% ({feePolicyDetails.effective.source === 'tenant_custom' ? 'custom' : 'inherited'}).
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={saveFeePolicy}
                disabled={feePolicyMutation.isLoading || feePolicyLoading}
                className="btn-secondary"
              >
                {feePolicyMutation.isLoading ? 'Saving...' : 'Save fee policy'}
              </button>
            </div>

            {feePolicyDetails?.history?.length > 0 && (
              <div className="md:col-span-2 border-b border-outline/70 pb-5">
                <h4 className="mb-2 text-xs font-semibold uppercase text-on-surface-variant">Recent fee history</h4>
                <div className="divide-y divide-outline/50 text-sm">
                  {feePolicyDetails.history.slice(0, 5).map((entry: any) => (
                    <div key={entry.id} className="grid gap-1 py-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <div>
                        <p className="font-medium text-on-surface">
                          {entry.mode === 'inherit' ? 'Platform default' : `${entry.rate_percent}% custom`}
                        </p>
                        {entry.reason && <p className="text-on-surface-variant">{entry.reason}</p>}
                        <p className="text-xs text-on-surface-variant">
                          Changed by {entry.actor?.name || entry.actor?.email || entry.created_by || 'system'}
                          {entry.created_at ? ` on ${new Date(entry.created_at).toLocaleString()}` : ''}
                        </p>
                      </div>
                      <div className="text-left text-on-surface-variant sm:text-right">
                        <p>Effective {new Date(entry.effective_from).toLocaleString()}</p>
                        {entry.agreement_reference && <p>{entry.agreement_reference}</p>}
                        {typeof entry.previous_policy_snapshot?.rate_basis_points === 'number' && (
                          <p className="text-xs">
                            Previous: {entry.previous_policy_snapshot.rate_basis_points / 100}%
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="md:col-span-2 pt-2">
              <div className="flex items-center gap-2 mb-3">
                <PaintBrushIcon className="h-5 w-5 text-violet-600" />
                <h3 className="text-sm font-semibold text-on-surface">Branding</h3>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">App Name</label>
              <input
                value={editForm.app_name}
                onChange={(event) => setEditForm({ ...editForm, app_name: event.target.value })}
                className="input-field"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-on-surface-variant">Logo</label>
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md border border-outline/70 bg-surface-container-low">
                  {editForm.logo_url ? (
                    <img
                      src={editForm.logo_url}
                      alt={`${editingName || 'Company'} logo`}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <BuildingOfficeIcon className="h-7 w-7 text-on-surface-variant" />
                  )}
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-outline/70 bg-surface px-3 py-2 text-sm font-medium text-on-surface hover:bg-surface-container-low">
                  <CloudArrowUpIcon className="h-4 w-4" />
                  {uploadLogoMutation.isLoading ? 'Uploading...' : 'Upload'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    disabled={uploadLogoMutation.isLoading}
                    onChange={(event) => {
                      uploadCompanyLogo(event.target.files?.[0]);
                      event.currentTarget.value = '';
                    }}
                  />
                </label>
                {editForm.logo_url && (
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, logo_url: '' })}
                    className="text-sm font-medium text-on-surface-variant hover:text-on-surface"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-10 w-14 rounded cursor-pointer"
                  value={editForm.primary_color || '#2563eb'}
                  onChange={(event) => setEditForm({ ...editForm, primary_color: event.target.value })}
                />
                <input
                  value={editForm.primary_color}
                  onChange={(event) => setEditForm({ ...editForm, primary_color: event.target.value })}
                  className="input-field flex-1"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-10 w-14 rounded cursor-pointer"
                  value={editForm.secondary_color || '#6b7280'}
                  onChange={(event) =>
                    setEditForm({ ...editForm, secondary_color: event.target.value })
                  }
                />
                <input
                  value={editForm.secondary_color}
                  onChange={(event) =>
                    setEditForm({ ...editForm, secondary_color: event.target.value })
                  }
                  className="input-field flex-1"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-on-surface-variant mb-1">Favicon URL</label>
              <input
                value={editForm.favicon_url}
                onChange={(event) => setEditForm({ ...editForm, favicon_url: event.target.value })}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditingName('');
                  setEditingSlug('');
                  setEditForm(INITIAL_EDIT_FORM);
                  setFeeForm(INITIAL_FEE_FORM);
                }}
                  className="px-4 py-2 text-sm font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-highest hover:text-on-surface rounded-md"
              >
                Cancel
              </button>
              <button onClick={saveEdit} disabled={updateMutation.isLoading} className="btn-primary">
                {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-card shadow-elevated max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-semibold text-on-surface">Delete Company</h3>
            </div>

            <p className="text-sm text-on-surface-variant mb-4">
              Delete <strong>{deleteTarget.name}</strong> from platform operations.
            </p>

            <label className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                checked={deletePermanent}
                onChange={(event) => {
                  setDeletePermanent(event.target.checked);
                  setDeleteConfirmName('');
                }}
                className="rounded border-outline text-error focus:ring-error"
              />
              <span className="text-sm font-medium text-on-error-container">Permanently delete company data</span>
            </label>

            {deletePermanent ? (
              <div className="mb-4">
                <p className="mb-2 text-sm text-on-error-container">
                  This permanently removes users and related records. Type the company name to confirm.
                </p>
                <input
                  type="text"
                  className="input-field"
                  placeholder={deleteTarget.name}
                  value={deleteConfirmName}
                  onChange={(event) => setDeleteConfirmName(event.target.value)}
                />
              </div>
            ) : (
              <p className="mb-4 text-sm text-on-surface-variant">
                Soft delete suspends the tenant and frees the slug without removing historical data.
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setDeletePermanent(false);
                  setDeleteConfirmName('');
                }}
                className="px-4 py-2 text-sm font-medium text-on-surface-variant bg-surface-container-low hover:bg-surface-container-highest hover:text-on-surface rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={
                  softDeleteMutation.isLoading ||
                  permanentDeleteMutation.isLoading ||
                  (deletePermanent && deleteConfirmName !== deleteTarget.name)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-error hover:bg-[#ef4444] rounded-md disabled:opacity-50"
              >
                {softDeleteMutation.isLoading || permanentDeleteMutation.isLoading
                  ? 'Deleting...'
                  : deletePermanent
                    ? 'Permanently Delete'
                    : 'Soft Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-table-card">
        <div className="admin-table-toolbar px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-on-surface">Tenant Operations</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              Each row surfaces lifecycle, billing readiness, user footprint, and access state.
            </p>
          </div>
          <div className="text-sm text-on-surface-variant">
            {companiesLoading ? 'Loading tenants...' : `${filteredCompanies.length} of ${companies.length} companies shown`}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="admin-table-grid min-w-full">
            <thead className="admin-table-head">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Company
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Operations
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Billing & Access
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Branding
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-on-surface-variant uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="admin-table-body">
              {companiesLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center">
                    <BuildingOfficeIcon className="mx-auto h-12 w-12 text-on-surface-variant" />
                    <h3 className="mt-2 text-sm font-medium text-on-surface">No companies match these filters</h3>
                    <p className="mt-1 text-sm text-on-surface-variant">
                      Adjust the search or lifecycle filters to broaden the operations view.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company: Company) => {
                  const companyInfo = getCompanyInfo(company);
                  const stripeState = getStripeState(company);
                  const accessState = accessStatusByCompany[company.id] || 'none';
                  const companyUrl = getCompanyUrl(company.slug);
                  const userCount = userCountByCompany[company.id] || 0;
                  const adminCount = adminCountByCompany[company.id] || 0;

                  return (
                    <tr key={company.id} className="admin-table-row align-top transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-start gap-3 min-w-[240px]">
                          {company.logo_url ? (
                            <img
                              src={company.logo_url}
                              alt={company.name}
                              className="h-11 w-11 rounded-lg object-cover admin-avatar-tile"
                            />
                          ) : (
                            <div className="admin-avatar-tile h-11 w-11 rounded-lg bg-info-container text-on-info-container flex items-center justify-center font-semibold">
                              {getInitials(company.name)}
                            </div>
                          )}
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="font-medium text-on-surface">{company.name}</div>
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(company.status)}`}>
                                {company.status}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-on-surface-variant">{getTenantHost(company.slug)}</div>
                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-on-surface-variant">
                              <span>{companyInfo.support_email || 'No support email'}</span>
                              <span>{companyInfo.timezone || 'Default timezone'}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 min-w-[220px]">
                        <div className="space-y-2 text-sm">
                          <div className="flex flex-wrap gap-2">
                            <span className="admin-soft-pill inline-flex px-2.5 py-1 rounded-full bg-info-container text-on-info-container font-medium text-xs">
                              {usersLoading ? 'Users ...' : `Users ${userCount}`}
                            </span>
                            <span className="admin-soft-pill inline-flex px-2.5 py-1 rounded-full bg-primary/15 text-on-surface font-medium text-xs">
                              {usersLoading ? 'Admins ...' : `Admins ${adminCount}`}
                            </span>
                          </div>
                          <div className="text-xs text-on-surface-variant">
                            Invoice prefix: <span className="font-medium text-on-surface">{companyInfo.invoice_prefix || 'INV'}</span>
                          </div>
                          <span className={`inline-flex w-fit px-2.5 py-1 rounded-full border border-outline/70 text-xs font-medium ${
                            company.email_sending_enabled === false
                              ? 'bg-error-container text-on-error-container'
                              : 'bg-success-container text-on-success-container'
                          }`}>
                            {company.email_sending_enabled === false ? 'Email sending off' : 'Email sending on'}
                          </span>
                          <div className="text-xs text-on-surface-variant">
                            {companyInfo.tax_id ? `Tax ID ${companyInfo.tax_id}` : 'Tax ID not configured'}
                          </div>
                          <div className="text-xs text-on-surface-variant line-clamp-2 -webkit-line-clamp-2 overflow-hidden">
                            {companyInfo.address || 'No operations address configured'}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 min-w-[220px]">
                        <div className="space-y-2">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStripeBadgeClass(stripeState)}`}>
                            {getStripeLabel(stripeState)}
                          </span>
                          <div className="text-xs text-on-surface-variant">
                            {company.stripe_account_id
                              ? `Stripe ${company.stripe_account_id}`
                              : 'Stripe has not been connected'}
                          </div>
                          <div className="text-xs text-on-surface-variant">
                            Platform fee:{' '}
                            <span className="font-medium text-on-surface">
                              {company.platform_fee_policy
                                ? `${company.platform_fee_policy.rate_percent}%`
                                : 'Unavailable'}
                            </span>{' '}
                            {company.platform_fee_policy && (
                              <>({company.platform_fee_policy.source === 'tenant_custom' ? 'custom' : 'default'})</>
                            )}
                          </div>
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getAccessBadgeClass(accessState)}`}>
                            {getAccessLabel(accessState)}
                          </span>
                          {accessState === 'none' || accessState === 'rejected' ? (
                            <button
                              onClick={() => requestAccessMutation.mutate(company.id)}
                              disabled={requestAccessMutation.isLoading}
                              className="admin-action-pill inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-info-container text-on-info-container hover:brightness-110 text-xs font-medium disabled:opacity-50"
                            >
                              <KeyIcon className="h-4 w-4" />
                              <span>{accessState === 'rejected' ? 'Request Again' : 'Request Access'}</span>
                            </button>
                          ) : null}
                        </div>
                      </td>

                      <td className="px-4 py-4 min-w-[220px]">
                        {hasBranding(company) ? (
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-on-surface">
                              {company.app_name || 'Custom branding'}
                            </div>
                            <div className="flex items-center gap-2">
                              {company.primary_color ? (
                                <span
                                  className="h-5 w-5 rounded-full admin-avatar-tile"
                                  style={{ backgroundColor: company.primary_color }}
                                  title={company.primary_color}
                                />
                              ) : null}
                              {company.secondary_color ? (
                                <span
                                  className="h-5 w-5 rounded-full admin-avatar-tile"
                                  style={{ backgroundColor: company.secondary_color }}
                                  title={company.secondary_color}
                                />
                              ) : null}
                              <span className="text-xs text-on-surface-variant">
                                {company.logo_url ? 'Logo set' : 'No logo'}
                              </span>
                            </div>
                            <div className="text-xs text-on-surface-variant">
                              {company.favicon_url ? 'Custom favicon configured' : 'Default favicon'}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <span className="inline-flex px-2.5 py-1 rounded-full bg-surface-container-low text-on-surface text-xs font-medium">
                              Default branding
                            </span>
                            <div className="text-xs text-on-surface-variant">
                              This tenant still uses shared brand defaults.
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4 min-w-[150px]">
                        <div className="text-sm text-on-surface">Created {formatDate(company.created_at)}</div>
                        <div className="mt-1 text-xs text-on-surface-variant">Updated {formatDate(company.updated_at)}</div>
                      </td>

                      <td className="px-4 py-4 min-w-[230px]">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            onClick={() => startEdit(company)}
                            className="admin-action-pill px-3 py-1.5 rounded-md bg-info-container text-on-info-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit
                          </button>
                          <a
                            href={companyUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 rounded-md bg-surface-container-low text-on-surface hover:bg-surface-container-highest text-sm font-medium inline-flex items-center gap-1"
                          >
                            <LinkIcon className="h-4 w-4" />
                            Open
                          </a>
                          {company.status === 'active' ? (
                            <button
                              onClick={() => {
                                if (window.confirm(`Suspend ${company.name}?`)) {
                                  suspendMutation.mutate(company.id);
                                }
                              }}
                              className="admin-action-pill px-3 py-1.5 rounded-md bg-warning-container text-on-warning-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1"
                            >
                              <PauseCircleIcon className="h-4 w-4" />
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => activateMutation.mutate(company.id)}
                              className="admin-action-pill px-3 py-1.5 rounded-md bg-success-container text-on-success-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1"
                            >
                              <PlayCircleIcon className="h-4 w-4" />
                              Activate
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteTarget(company)}
                            className="admin-action-pill px-3 py-1.5 rounded-md bg-error-container text-on-error-container hover:brightness-110 text-sm font-medium inline-flex items-center gap-1"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
}

export default AdminCompanies;
